import { useEffect, useState } from 'react';
import {
  X,
  Calendar,
  Users,
  MapPin,
  Phone,
  MessageSquare,
  Check,
  AlertCircle,
  Navigation,
  ChevronRight,
  Star,
  Home,
  TreePine,
  Plus,
  Minus,
  ChevronLeft,
  SunMedium,
  Moon,
  ChevronDown,
  ChevronUp,
  Armchair,
  Sparkles
} from 'lucide-react';
import type { Restaurant } from '../../App';
import { api } from '../../lib/api';
import { ImageWithFallback } from '../figma/ImageWithFallback';

interface RestaurantSeatingPreference {
  id: string;
  seatingPreferenceId: string;
  isAvailable: boolean;
  seatingPreference: {
    id: string;
    name: string;
    code: string;
    isActive: boolean;
  };
}

interface BookTableModalProps {
  restaurant: Restaurant;
  seatingPreferences: RestaurantSeatingPreference[];
  onClose: () => void;
}

type BookingStep = 'details' | 'guest-info' | 'confirmation' | 'error';
type ErrorType = 'fully-booked' | 'slot-unavailable' | 'invalid-phone' | 'network-error' | null;
type MealPeriod = 'lunch' | 'dinner';

interface BookingData {
  date: string;
  time: string;
  guests: number;
  seatingPreferenceId: string;
  name: string;
  phone: string;
  email: string;
  specialRequests: string;
}

interface SlotSection {
  key: MealPeriod;
  label: string;
  hoursLabel: string;
  startMinutes: number;
  lastBookingMinutes: number;
}

const SLOT_INTERVAL_MINUTES = 15;

const SLOT_SECTIONS: SlotSection[] = [
  {
    key: 'lunch',
    label: 'Lunch',
    hoursLabel: '11:00 AM to 03:00 PM',
    startMinutes: 11 * 60,
    lastBookingMinutes: 14 * 60 + 45
  },
  {
    key: 'dinner',
    label: 'Dinner',
    hoursLabel: '06:00 PM to 12:00 AM',
    startMinutes: 18 * 60,
    lastBookingMinutes: 23 * 60 + 45
  }
];

const toDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMinutesToTime = (totalMinutes: number) => {
  const normalizedMinutes = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
  const hours24 = Math.floor(normalizedMinutes / 60);
  const minutes = normalizedMinutes % 60;
  const period = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;

  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

const parseTimeToMinutes = (time: string) => {
  const [clock, period] = time.split(' ');
  const [hoursText, minutesText] = clock.split(':');
  let hours = Number(hoursText);
  const minutes = Number(minutesText);

  if (period === 'PM' && hours !== 12) {
    hours += 12;
  }

  if (period === 'AM' && hours === 12) {
    hours = 0;
  }

  return hours * 60 + minutes;
};

const normalizeCode = (value: string) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

const getSeatingIcon = (code: string) => {
  const normalized = normalizeCode(code);
  if (normalized.includes('INDOOR')) return Home;
  if (normalized.includes('OUTDOOR')) return TreePine;
  if (normalized.includes('ANY')) return Users;
  if (normalized.includes('ROOFTOP') || normalized.includes('WINDOW')) return Sparkles;
  return Armchair;
};

export function BookTableModal({ restaurant, seatingPreferences, onClose }: BookTableModalProps) {
  const [step, setStep] = useState<BookingStep>('details');
  const [errorType, setErrorType] = useState<ErrorType>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [expandedSection, setExpandedSection] = useState<MealPeriod | null>(null);
  const [currentTime, setCurrentTime] = useState(() => new Date());

  const [bookingData, setBookingData] = useState<BookingData>({
    date: '',
    time: '',
    guests: 2,
    seatingPreferenceId: '',
    name: '',
    phone: '',
    email: '',
    specialRequests: ''
  });

  useEffect(() => {
    if (!seatingPreferences.length) return;

    setBookingData((prev) => {
      if (prev.seatingPreferenceId) return prev;

      const anyOption =
        seatingPreferences.find((item) => normalizeCode(item.seatingPreference.code) === 'ANY') ||
        seatingPreferences[0];

      return {
        ...prev,
        seatingPreferenceId: anyOption?.seatingPreferenceId || '',
      };
    });
  }, [seatingPreferences]);

  // Generate dates
  const today = currentTime;
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const dateOptions = [
    { label: 'Today', value: toDateKey(today), display: today.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) },
    { label: 'Tomorrow', value: toDateKey(tomorrow), display: tomorrow.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) },
  ];

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  const selectedDate = bookingData.date ? new Date(`${bookingData.date}T00:00:00`) : null;
  const isSelectedDateToday = bookingData.date === toDateKey(currentTime);
  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();

  const getSectionSlots = (section: SlotSection) => {
    const slots: string[] = [];

    for (
      let minutes = section.startMinutes;
      minutes <= section.lastBookingMinutes;
      minutes += SLOT_INTERVAL_MINUTES
    ) {
      slots.push(formatMinutesToTime(minutes));
    }

    return slots;
  };

  const getSectionStatus = (section: SlotSection) => {
    if (!isSelectedDateToday) {
      return {
        isOpen: true,
        label: 'Open',
        helper: `Last slot ${formatMinutesToTime(section.lastBookingMinutes)}`
      };
    }

    if (currentMinutes > section.lastBookingMinutes) {
      return {
        isOpen: false,
        label: 'Closed',
        helper: 'Booking closed for today'
      };
    }

    if (currentMinutes < section.startMinutes) {
      return {
        isOpen: true,
        label: 'Open',
        helper: `Opens at ${formatMinutesToTime(section.startMinutes)}`
      };
    }

    return {
      isOpen: true,
      label: 'Open',
      helper: `Last slot ${formatMinutesToTime(section.lastBookingMinutes)}`
    };
  };

  const isSlotDisabled = (time: string, section: SlotSection) => {
    const slotMinutes = parseTimeToMinutes(time);

    if (!selectedDate) {
      return false;
    }

    const selectedDateKey = toDateKey(selectedDate);
    const todayKey = toDateKey(currentTime);

    if (selectedDateKey !== todayKey) {
      return false;
    }

    if (currentMinutes > section.lastBookingMinutes) {
      return true;
    }

    return slotMinutes < currentMinutes;
  };

  useEffect(() => {
    if (!bookingData.time || !selectedDate) {
      return;
    }

    const selectedSection = SLOT_SECTIONS.find(section =>
      getSectionSlots(section).includes(bookingData.time)
    );

    if (selectedSection && isSlotDisabled(bookingData.time, selectedSection)) {
      setBookingData(prev => ({ ...prev, time: '' }));
    }
  }, [bookingData.time, bookingData.date, currentTime]);

  useEffect(() => {
    if (!isSelectedDateToday) {
      return;
    }

    const lunchStatus = getSectionStatus(SLOT_SECTIONS[0]);
    const dinnerStatus = getSectionStatus(SLOT_SECTIONS[1]);

    if (!lunchStatus.isOpen && dinnerStatus.isOpen) {
      setExpandedSection('dinner');
    }
  }, [isSelectedDateToday, currentTime]);

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const isDateDisabled = (day: number | null) => {
    if (day === null) return true;
    
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    return date < todayStart;
  };

  const getDateValue = (day: number) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return toDateKey(date);
  };

  const isToday = (day: number | null) => {
    if (day === null) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toDateString() === today.toDateString();
  };

  const isTomorrow = (day: number | null) => {
    if (day === null) return false;
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date.toDateString() === tomorrow.toDateString();
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const canGoPrevious = () => {
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    return prevMonth >= new Date(today.getFullYear(), today.getMonth(), 1);
  };

  const handleContinue = () => {
    if (bookingData.date && bookingData.time && bookingData.guests > 0) {
      setStep('guest-info');
    }
  };

  const handleRetry = () => {
    setErrorType(null);
    setErrorMessage('');
    setStep(errorType === 'invalid-phone' ? 'guest-info' : 'details');
  };

  const handleCall = () => {
    window.location.href = `tel:${restaurant.phone}`;
  };

  const handleDirections = () => {
    window.open(`https://maps.google.com/?q=${encodeURIComponent(restaurant.address)}`, '_blank');
  };

  const selectedDateLabel =
    dateOptions.find(d => d.value === bookingData.date)?.label ??
    (bookingData.date
      ? new Date(`${bookingData.date}T00:00:00`).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        })
      : '');

  const selectedDateDisplay =
    dateOptions.find(d => d.value === bookingData.date)?.display ??
    (bookingData.date
      ? new Date(`${bookingData.date}T00:00:00`).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        })
      : '');

  const isDetailsValid = bookingData.date && bookingData.time && bookingData.guests > 0;
  const isGuestInfoValid = bookingData.name.trim() && bookingData.phone.trim();
  const selectedSeatingPreference = seatingPreferences.find(
    (item) => item.seatingPreferenceId === bookingData.seatingPreferenceId
  );

  const submitBooking = async () => {
    if (bookingData.phone.length < 10) {
      setErrorType('invalid-phone');
      setErrorMessage('Please enter a valid 10-digit mobile number.');
      setStep('error');
      return;
    }

    try {
      setSubmitting(true);
      setErrorType(null);
      setErrorMessage('');

      const response = await api.bookings.create({
        restaurantId: restaurant.id,
        bookingDate: bookingData.date,
        bookingTime: bookingData.time,
        guestCount: bookingData.guests,
        seatingPreferenceId: bookingData.seatingPreferenceId || undefined,
        name: bookingData.name,
        phone: bookingData.phone,
        email: bookingData.email || undefined,
        specialRequests: bookingData.specialRequests || undefined,
      });

      setBookingId(String(response?.data?.bookingNumber || response?.data?.id || ''));
      setStep('confirmation');
    } catch (error: any) {
      const message = String(error?.message || 'Unable to complete your booking right now.');
      const cleanMessage = message.replace(/^\[\d+\]\s*/, '');

      setErrorMessage(cleanMessage);
      if (message.includes('[409]')) {
        setErrorType('fully-booked');
      } else if (cleanMessage.toLowerCase().includes('slot')) {
        setErrorType('slot-unavailable');
      } else {
        setErrorType('network-error');
      }

      setStep('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white w-full md:max-w-2xl md:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-y-auto dark:bg-slate-950">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10 rounded-t-3xl dark:bg-slate-950 dark:border-slate-800">
          <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100">
            {step === 'details' && 'Book a Table'}
            {step === 'guest-info' && 'Guest Details'}
            {step === 'confirmation' && 'Booking Confirmed!'}
            {step === 'error' && 'Booking Failed'}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors dark:bg-slate-800 dark:hover:bg-slate-700"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-slate-300" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Restaurant Header */}
          {step !== 'error' && (
            <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-slate-800">
              <ImageWithFallback
                src={restaurant.logo}
                alt={restaurant.name}
                className="w-16 h-16 rounded-xl object-cover border-2 border-gray-200"
              />
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 text-lg dark:text-slate-100">{restaurant.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex items-center gap-1 bg-[#4ADE80] text-white px-2 py-0.5 rounded-md">
                    <Star className="w-3 h-3 fill-white" />
                    <span className="text-sm font-bold">{restaurant.rating}</span>
                  </div>
                  <span className="text-sm text-gray-600 dark:text-slate-400">{restaurant.address.split(',')[0]}</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Booking Details */}
          {step === 'details' && (
            <div className="space-y-6">
              {/* Date Selector - Calendar View */}
              <div>
                <label className="block text-base font-normal text-gray-900 mb-3 dark:text-slate-100">
                  Select Date <span className="text-red-500">*</span>
                </label>
                
                {/* Selected Date Display with Calendar Toggle */}
                <div 
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="bg-white border-2 border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between cursor-pointer hover:border-[#60A5FA] transition-colors dark:bg-slate-900 dark:border-slate-700 dark:hover:border-blue-400"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-gray-600 dark:text-slate-300">
                      {bookingData.date ? (
                        <div>
                          <div className="text-xs text-gray-500 dark:text-slate-400">Selected Date</div>
                          <div className="font-semibold text-gray-900 dark:text-slate-100">
                            {new Date(bookingData.date).toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-500 dark:text-slate-400">Click to select a date</div>
                      )}
                    </div>
                  </div>
                  <Calendar className={`w-5 h-5 transition-colors ${showCalendar ? 'text-[#60A5FA]' : 'text-gray-400 dark:text-slate-500'}`} />
                </div>

                {/* Calendar Dropdown */}
                {showCalendar && (
                  <div className="mt-3 bg-white border-2 border-gray-200 rounded-xl p-3 shadow-lg dark:bg-slate-900 dark:border-slate-700">
                    <div className="flex items-center justify-between mb-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          previousMonth();
                        }}
                        disabled={!canGoPrevious()}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed dark:hover:bg-slate-800"
                      >
                        <ChevronLeft className="w-4 h-4 text-gray-700 dark:text-slate-200" />
                      </button>
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          nextMonth();
                        }}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors dark:hover:bg-slate-800"
                      >
                        <ChevronRight className="w-4 h-4 text-gray-700 dark:text-slate-200" />
                      </button>
                    </div>

                    {/* Day Headers */}
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-xs font-medium text-gray-500 py-1 dark:text-slate-400">
                          {day}
                        </div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 gap-1">
                      {generateCalendarDays().map((day, index) => {
                        if (day === null) {
                          return <div key={`empty-${index}`} className="aspect-square" />;
                        }

                        const dateValue = getDateValue(day);
                        const isSelected = bookingData.date === dateValue;
                        const disabled = isDateDisabled(day);
                        const todayDate = isToday(day);
                        const tomorrowDate = isTomorrow(day);

                        return (
                          <button
                            key={day}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!disabled) {
                                setBookingData({ ...bookingData, date: dateValue });
                                setShowCalendar(false);
                              }
                            }}
                            disabled={disabled}
                            className={`aspect-square rounded-lg text-xs font-medium transition-all flex items-center justify-center ${
                              disabled
                                ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                                : isSelected
                                ? 'bg-[#60A5FA] text-white shadow-md'
                                : 'bg-white border border-gray-200 hover:border-[#60A5FA] hover:bg-blue-50 text-gray-900 dark:bg-slate-900 dark:border-slate-700 dark:hover:border-blue-400 dark:hover:bg-slate-800 dark:text-slate-100'
                            }`}
                          >
                            <div className="flex flex-col items-center justify-center">
                              <span className="text-sm">{day}</span>
                              {todayDate && !isSelected && (
                                <span className="text-[9px] text-[#60A5FA] font-semibold leading-none">Today</span>
                              )}
                              {tomorrowDate && !isSelected && (
                                <span className="text-[9px] text-green-600 font-semibold leading-none">Tmrw</span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Time Slots */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3 dark:text-slate-100">
                  Select Time <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  {SLOT_SECTIONS.map(section => {
                    const slots = getSectionSlots(section);
                    const status = getSectionStatus(section);
                    const Icon = section.key === 'lunch' ? SunMedium : Moon;
                    const isExpanded = expandedSection === section.key;

                    return (
                      <div
                        key={section.key}
                        className="rounded-2xl border border-gray-200 bg-white dark:border-slate-700 dark:bg-slate-900"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedSection(current => (current === section.key ? null : section.key))
                          }
                          className="w-full px-4 py-4 flex items-start justify-between text-left"
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-200">
                              <Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                                  {section.label}
                                </h3>
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                                    status.isOpen
                                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                                      : 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300'
                                  }`}
                                >
                                  {status.label}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-gray-600 dark:text-slate-300">
                                {section.hoursLabel}
                              </p>
                              <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                                {bookingData.date
                                  ? status.helper
                                  : 'Select a date to view live availability'}
                              </p>
                            </div>
                          </div>
                          <div className="mt-1 text-gray-500 dark:text-slate-400">
                            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="px-4 pb-4">
                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                              {slots.map(slot => {
                                const isSelected = bookingData.time === slot;
                                const disabled = bookingData.date ? isSlotDisabled(slot, section) : false;

                                return (
                                  <button
                                    key={`${section.key}-${slot}`}
                                    type="button"
                                    disabled={disabled || !bookingData.date}
                                    onClick={() => setBookingData({ ...bookingData, time: slot })}
                                    className={`rounded-xl border px-3 py-3 text-center transition-all ${
                                      isSelected
                                        ? 'border-[#60A5FA] bg-blue-50 text-[#60A5FA] dark:border-blue-400 dark:bg-blue-500/10 dark:text-blue-300'
                                        : disabled
                                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed dark:border-slate-800 dark:bg-slate-950 dark:text-slate-600'
                                        : !bookingData.date
                                        ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed dark:border-slate-800 dark:bg-slate-950 dark:text-slate-600'
                                        : 'border-gray-200 bg-white text-gray-900 hover:border-gray-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-slate-500'
                                    }`}
                                  >
                                    <div className="text-base font-semibold">{slot}</div>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Number of Guests */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3 dark:text-slate-100">
                  Number of Guests <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setBookingData({ ...bookingData, guests: Math.max(1, bookingData.guests - 1) })}
                    className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors disabled:opacity-50 dark:bg-slate-800 dark:hover:bg-slate-700"
                    disabled={bookingData.guests <= 1}
                  >
                    <Minus className="w-5 h-5 text-gray-700 dark:text-slate-200" />
                  </button>
                  <div className="flex-1 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Users className="w-6 h-6 text-[#60A5FA]" />
                      <span className="text-3xl font-bold text-gray-900 dark:text-slate-100">{bookingData.guests}</span>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-slate-400">
                      {bookingData.guests === 1 ? 'Guest' : 'Guests'}
                    </span>
                  </div>
                  <button
                    onClick={() => setBookingData({ ...bookingData, guests: Math.min(20, bookingData.guests + 1) })}
                    className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors disabled:opacity-50 dark:bg-slate-800 dark:hover:bg-slate-700"
                    disabled={bookingData.guests >= 20}
                  >
                    <Plus className="w-5 h-5 text-gray-700 dark:text-slate-200" />
                  </button>
                </div>
              </div>

              {/* Seating Preference */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Seating Preference
                </label>
                {seatingPreferences.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {seatingPreferences.map((item) => {
                      const SeatingIcon = getSeatingIcon(item.seatingPreference.code);
                      const isSelected = bookingData.seatingPreferenceId === item.seatingPreferenceId;

                      return (
                        <button
                          key={item.id}
                          onClick={() =>
                            setBookingData({ ...bookingData, seatingPreferenceId: item.seatingPreferenceId })
                          }
                          className={`p-4 border-2 rounded-xl transition-all ${
                            isSelected
                              ? 'border-[#60A5FA] bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <SeatingIcon className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-[#60A5FA]' : 'text-gray-400'}`} />
                          <div className="text-sm font-medium text-gray-900 text-center">
                            {item.seatingPreference.name}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500">
                    No seating preferences are configured for this restaurant yet.
                  </div>
                )}
              </div>

              {/* Continue Button */}
              <button
                onClick={handleContinue}
                disabled={!isDetailsValid}
                className={`w-full py-4 rounded-xl font-semibold text-white transition-all flex items-center justify-center gap-2 ${
                  isDetailsValid
                    ? 'bg-[#60A5FA] hover:bg-[#3B82F6] shadow-lg shadow-blue-200'
                    : 'bg-gray-300 cursor-not-allowed'
                }`}
              >
                <span>Continue</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Step 2: Guest Info */}
          {step === 'guest-info' && (
            <div className="space-y-6">
              {/* Booking Summary */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Booking Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Date</span>
                    <span className="font-medium text-gray-900">{selectedDateLabel}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Time</span>
                    <span className="font-medium text-gray-900">{bookingData.time}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Guests</span>
                    <span className="font-medium text-gray-900">{bookingData.guests} {bookingData.guests === 1 ? 'Guest' : 'Guests'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Seating</span>
                    <span className="font-medium text-gray-900">
                      {selectedSeatingPreference?.seatingPreference.name || 'Not selected'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={bookingData.name}
                  onChange={(e) => setBookingData({ ...bookingData, name: e.target.value })}
                  placeholder="Enter your full name"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#60A5FA] focus:outline-none transition-colors"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="w-20 px-3 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 flex items-center justify-center font-medium text-gray-700">
                    +91
                  </div>
                  <input
                    type="tel"
                    value={bookingData.phone}
                    onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="Enter 10-digit mobile number"
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#60A5FA] focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Email <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="email"
                  value={bookingData.email}
                  onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
                  placeholder="your.email@example.com"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#60A5FA] focus:outline-none transition-colors"
                />
              </div>

              {/* Special Requests */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Special Requests <span className="text-gray-500 text-xs">(Optional)</span>
                </label>
                <textarea
                  value={bookingData.specialRequests}
                  onChange={(e) => setBookingData({ ...bookingData, specialRequests: e.target.value })}
                  placeholder="E.g., Birthday celebration, window seat, anniversary..."
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#60A5FA] focus:outline-none transition-colors resize-none"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setStep('details')}
                  className="flex-1 py-4 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={submitBooking}
                  disabled={!isGuestInfoValid || submitting}
                  className={`flex-1 py-4 rounded-xl font-semibold text-white transition-all ${
                    isGuestInfoValid && !submitting
                      ? 'bg-[#4ADE80] hover:bg-[#22C55E] shadow-lg shadow-green-200'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  {submitting ? 'Confirming...' : 'Confirm Booking'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 'confirmation' && (
            <div className="text-center space-y-6">
              {/* Success Icon */}
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-[#4ADE80] to-[#22C55E] rounded-full flex items-center justify-center mb-4">
                <Check className="w-10 h-10 text-white stroke-[3]" />
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Table Booked Successfully! 🎉</h3>
                <p className="text-gray-600">
                  Your reservation has been confirmed. You'll receive a confirmation SMS shortly.
                </p>
              </div>

              {/* Booking Details Card */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 text-left">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-blue-200">
                  <h4 className="font-bold text-gray-900">Booking Details</h4>
                  <div className="bg-white px-3 py-1 rounded-lg">
                    <span className="text-xs text-gray-500">Booking ID</span>
                    <p className="font-bold text-[#60A5FA]">{bookingId || 'Pending'}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-[#60A5FA]" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Date & Time</div>
                      <div className="font-semibold text-gray-900">
                        {selectedDateDisplay} • {bookingData.time}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                      {(() => {
                        const SeatingIcon = getSeatingIcon(selectedSeatingPreference?.seatingPreference.code || '');
                        return <SeatingIcon className="w-5 h-5 text-[#60A5FA]" />;
                      })()}
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Seating</div>
                      <div className="font-semibold text-gray-900">
                        {selectedSeatingPreference?.seatingPreference.name || 'Any'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                      <Users className="w-5 h-5 text-[#60A5FA]" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Guests</div>
                      <div className="font-semibold text-gray-900">
                        {bookingData.guests} {bookingData.guests === 1 ? 'Person' : 'People'}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#60A5FA]" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-600">Restaurant</div>
                      <div className="font-semibold text-gray-900">{restaurant.name}</div>
                    </div>
                  </div>

                  {bookingData.specialRequests && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                        <MessageSquare className="w-5 h-5 text-[#60A5FA]" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">Special Request</div>
                        <div className="font-medium text-gray-900">{bookingData.specialRequests}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleDirections}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-[#60A5FA] text-white rounded-xl hover:bg-[#3B82F6] transition-colors font-medium"
                >
                  <Navigation className="w-5 h-5" />
                  <span>Directions</span>
                </button>
                <button
                  onClick={handleCall}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-[#4ADE80] text-white rounded-xl hover:bg-[#22C55E] transition-colors font-medium"
                >
                  <Phone className="w-5 h-5" />
                  <span>Call</span>
                </button>
              </div>

              <button
                onClick={onClose}
                className="w-full py-3 text-gray-700 font-medium hover:text-gray-900 transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {/* Error State */}
          {step === 'error' && (
            <div className="text-center space-y-6">
              {/* Error Icon */}
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-10 h-10 text-white stroke-[3]" />
              </div>

              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {errorType === 'fully-booked' && 'Restaurant Fully Booked'}
                  {errorType === 'slot-unavailable' && 'Time Slot Unavailable'}
                  {errorType === 'invalid-phone' && 'Invalid Phone Number'}
                  {errorType === 'network-error' && 'Booking Failed'}
                </h3>
                <p className="text-gray-600">{errorMessage || 'Unable to complete your booking. Please try again.'}</p>
              </div>

              {/* Error Illustration */}
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8">
                <div className="text-6xl mb-4">
                  {errorType === 'fully-booked' && '🚫'}
                  {errorType === 'slot-unavailable' && '⏰'}
                  {errorType === 'invalid-phone' && '📱'}
                  {errorType === 'network-error' && '📡'}
                </div>
                <p className="text-sm text-gray-700">
                  {errorType === 'fully-booked' && 'All tables are reserved'}
                  {errorType === 'slot-unavailable' && 'Time slot taken'}
                  {errorType === 'invalid-phone' && 'Check your number'}
                  {errorType === 'network-error' && 'Connection lost'}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleRetry}
                  className="w-full py-4 bg-[#60A5FA] text-white rounded-xl hover:bg-[#3B82F6] transition-colors font-semibold"
                >
                  {errorType === 'invalid-phone' ? 'Fix Phone Number' : 'Try Different Slot'}
                </button>
                <button
                  onClick={handleCall}
                  className="w-full py-4 bg-white border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-semibold flex items-center justify-center gap-2"
                >
                  <Phone className="w-5 h-5" />
                  <span>Call Restaurant Directly</span>
                </button>
                <button
                  onClick={onClose}
                  className="w-full py-3 text-gray-700 font-medium hover:text-gray-900 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
